// ============================================================================
// src/pages/VerifyResetPage.tsx
// ============================================================================
import React, { useState, useEffect, useCallback } from "react";
import { useVerifyResetTokenMutation } from "../services/auth";
import {
  useNavigate,
  useLocation,
  useSearchParams,
  Link,
} from "react-router-dom";

export default function VerifyResetPage() {
  const [verifyResetToken, { isLoading }] = useVerifyResetTokenMutation();
  const navigate = useNavigate();
  const loc = useLocation();
  const [searchParams] = useSearchParams();

  // Try to get email and token from URL params first, then from location state
  const [email, setEmail] = useState(
    searchParams.get("email") || (loc.state as any)?.email || "",
  );
  const [token, setToken] = useState(searchParams.get("token") || "");
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  // Auto-verify if both email and token are present in URL
  const autoVerify = useCallback(
    async (emailParam: string, tokenParam: string) => {
      try {
        await verifyResetToken({
          email: emailParam,
          token: tokenParam,
        }).unwrap();
        // Token is valid, navigate to reset password page
        navigate("/reset-password", {
          state: { email: emailParam, token: tokenParam },
        });
      } catch (err: any) {
        setError(
          err?.data?.error ||
            err?.data?.message ||
            "Invalid or expired reset link",
        );
        setVerifying(false);
      }
    },
    [verifyResetToken, navigate],
  );

  useEffect(() => {
    const emailParam = searchParams.get("email");
    const tokenParam = searchParams.get("token");

    if (emailParam && tokenParam && !verifying) {
      setVerifying(true);
      autoVerify(emailParam, tokenParam);
    }
  }, [searchParams, autoVerify, verifying]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email || !token) {
      setError("Please provide both email and reset token.");
      return;
    }

    try {
      await verifyResetToken({ email, token }).unwrap();
      // Token valid — go to reset password page
      navigate("/reset-password", { state: { email, token } });
    } catch (err: any) {
      setError(
        err?.data?.error || err?.data?.message || "Invalid or expired token",
      );
    }
  }

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white shadow-lg rounded-lg p-8"
      >
        <h2 className="text-2xl font-bold mb-2 text-gray-900">
          Verify reset token
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Enter your email and the reset token from your email.
        </p>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reset token
          </label>
          <input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
            placeholder="Paste the token from your email"
          />
        </div>

        <button
          type="submit"
          className="w-full px-4 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-60 transition-colors"
          disabled={isLoading}
        >
          {isLoading ? "Verifying..." : "Verify token"}
        </button>

        <div className="mt-6 text-center space-y-2">
          <Link
            to="/forgot-password"
            className="block text-sm text-teal-600 hover:text-teal-700 font-medium"
          >
            Request a new reset link
          </Link>
          <Link
            to="/login"
            className="block text-sm text-gray-600 hover:text-gray-700"
          >
            Back to login
          </Link>
        </div>
      </form>
    </div>
  );
}
